// src/pages/GuestJoinSessionPage/GuestJoinSessionPage.jsx
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAllEventSessionsTables } from "@/Services/EventServices";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isValidPhone = (phone) => /^\+?[0-9()\-\s]{7,20}$/.test(phone);

const API_BASE =
  import.meta.env.VITE_API_BASE_URL || "https://api.theglobalapp.com";

// ✅ Public call (NO Bearer token)
async function joinGuestNoAuth(sessionId, payload) {
  const res = await fetch(
    `${API_BASE}/api/v1/user/events/session/${sessionId}/join-guest`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" }, // ✅ no Authorization
      body: JSON.stringify(payload),
    },
  );

  const json = await res.json();
  if (!res.ok) throw new Error(json?.message || "Failed to join session");
  return json; // { success, message, data: { token, user, sessionId, event } }
}

const lobbyUrlFor = (eventId, sessionId) => {
  const sid = encodeURIComponent(sessionId || "");
  if (eventId)
    return `/lobby?event=${encodeURIComponent(eventId)}&session=${sid}`;
  return `/lobby?session=${sid}`; // fallback
};

export default function GuestJoinSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { applyAuth } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "" });

  // If user already logged in, try to resolve eventId and redirect immediately
  useEffect(() => {
    if (!sessionId) {
      navigate("/events", { replace: true });
      return;
    }

    const token = localStorage.getItem("authToken"); // ✅ your app uses authToken
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        // Try to find the eventId for this sessionId so we can redirect to the
        // existing lobby URL format without changing Lobby.jsx.
        const res = await getAllEventSessionsTables();
        const payload = res?.data?.data || res?.data || res;
        const list = payload?.data || payload; // tolerate different shapes

        let eventId = null;

        if (Array.isArray(list)) {
          // list items typically look like: { session, tables: [...] }
          for (const item of list) {
            const sid =
              item?.session?._id ||
              item?.sessionId ||
              item?.session?._id ||
              item?._id;

            if (String(sid) !== String(sessionId)) continue;

            // eventId may exist in different places depending on backend shape
            eventId =
              item?.event?._id ||
              item?.eventId ||
              item?.session?.eventId ||
              item?.tables?.[0]?.eventId ||
              null;

            if (eventId) break;
          }
        }

        if (!cancelled) {
          navigate(lobbyUrlFor(eventId, sessionId), { replace: true });
        }
      } catch {
        // Fallback: at least send them to lobby with session param
        if (!cancelled) {
          navigate(lobbyUrlFor(null, sessionId), { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const name = form.name.trim();
      if (!name) return toast.error("Name is required");

      try {
        setSubmitting(true);

        const res = await joinGuestNoAuth(sessionId, { name });

        if (!res?.success)
          throw new Error(res?.message || "Failed to join meeting");

        const { token, user, sessionId: sid, event } = res.data || {};
        if (!token) throw new Error("Token was not returned");

        applyAuth(token, user);
        navigate(lobbyUrlFor(event?._id, sid || sessionId), { replace: true });
      } catch (err) {
        toast.error(err?.message || "Failed to join meeting");
      } finally {
        setSubmitting(false);
      }
    },
    [applyAuth, form, navigate, sessionId],
  );

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })),
  });

  return (
    <div className="min-h-[80vh]">
      <div className="container-x grid md:grid-cols-12 gap-12 py-16 md:py-24">
        {/* ── Left panel ── */}
        <div className="md:col-span-5 hidden md:block">
          <p className="overline mb-5">Guest Access</p>
          <h1 className="font-display font-light text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
            Join the session,
            <br />
            <span className="italic text-[#C6A87C]">no account needed.</span>
          </h1>
          <p className="text-white/65 max-w-md leading-relaxed">
            Fill in your Name to get instant access to this meeting
            session as a guest.
          </p>

          {/* Field summary cards */}
          <div className="mt-12 space-y-3">
            {[
              {
                icon: User,
                label: "Identity",
                value:
                  form.firstName || form.lastName
                    ? `${form.firstName} ${form.lastName}`.trim()
                    : null,
                placeholder: "Full name",
              },
              // {
              //   icon: Mail,
              //   label: "Email",
              //   value: form.email || null,
              //   placeholder: "Your email address",
              // },
              // {
              //   icon: Phone,
              //   label: "Phone",
              //   value: form.phoneNumber || null,
              //   placeholder: "Your phone number",
              // },
              // {
              //   icon: Building2,
              //   label: "Company",
              //   value: form.company || null,
              //   placeholder: "Optional",
              // },
              // {
              //   icon: Briefcase,
              //   label: "Designation",
              //   value: form.designation || null,
              //   placeholder: "Optional",
              // },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 text-sm transition-opacity ${
                    s.value ? "opacity-100" : "opacity-40"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      s.value ? "bg-[#C6A87C]" : "bg-white/20"
                    }`}
                  />
                  <Icon
                    size={13}
                    className={s.value ? "text-[#C6A87C]" : "text-white/30"}
                  />
                  <span className="text-white/70">{s.label}</span>
                  {s.value ? (
                    <span className="text-[#C6A87C] text-xs truncate max-w-[160px]">
                      — {s.value}
                    </span>
                  ) : (
                    <span className="text-white/25 text-xs">
                      — {s.placeholder}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="md:col-span-6 md:col-start-7">
          <div className="glass rounded-2xl p-8">
            {/* Header */}
            <div className="mb-8">
              <p className="overline mb-1">Guest Join</p>
              <h2 className="font-display text-3xl tracking-tight">
                Enter your name
              </h2>
              <p className="text-white/50 text-sm mt-1">
                Tell us who you are to join this session
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              {/* First + Last name */}
              <div className="grid md:grid-cols-1 gap-4">
                <label className="block">
  <span className="overline block mb-2">Full Name *</span>
  <input
    required
    value={form.name}
    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
    placeholder="John Doe"
    className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-4 py-3
               focus:border-[#C6A87C] focus:outline-none transition-colors
               text-white placeholder:text-white/30 text-sm"
  />
</label>
              </div>

              {/* Hint box */}
              <div className="flex items-start gap-2 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                <User
                  size={16}
                  className="text-[#C6A87C] mt-0.5 flex-shrink-0"
                />
                <p className="text-white/40 text-xs leading-relaxed">
                  Your name is only used to identify you inside this
                  session.
                </p>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Joining…" : "Join Session"}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
